import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import LoginPage from "./pages/LoginPage";
import PublicRegistrationPage from "./pages/PublicRegistrationPage";
import DashboardPage from "./pages/DashboardPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import IndicatorsPage from "./pages/IndicatorsPage";
import UsuariosPage from "./pages/UsuariosPage";
import CommissionsPage from "./pages/CommissionsPage";
import ClientesPage from "./pages/ClientesPage";
import ContratosPage from "./pages/ContratosPage";
import VencimentosPage from "./pages/VencimentosPage";
import FinancialDashboardPage from "./pages/FinancialDashboardPage";
import SociosPage from "./pages/SociosPage";
import TreasuryPage from "./pages/TreasuryPage";
import ForecastPage from "./pages/ForecastPage";
import ImportPage from "./pages/ImportPage";
import BlockedClientesPage from "./pages/BlockedClientesPage";
import ParceladosPage from "./pages/ParceladosPage";
import ParceladoDetailPage from "./pages/ParceladoDetailPage";
import CobrancaPage from "./pages/CobrancaPage";
import ExecutiveDashboardPage from "./pages/ExecutiveDashboardPage";
import CapitalSourcePage from "./pages/CapitalSourcePage";
import RecalcStatusPage from "./pages/RecalcStatusPage";
import HighInterestPage from "./pages/HighInterestPage";
import AuditoriaPage from "./pages/AuditoriaPage";
import RecuperacaoPage from "./pages/RecuperacaoPage";
import FechamentoPage from "./pages/FechamentoPage";
import WhatsAppPage from "./pages/WhatsAppPage";
import IntegracoesPage from "./pages/IntegracoesPage";
import IntegracaoDetailPage from "./pages/IntegracaoDetailPage";
import ComunicacaoPage from "./pages/ComunicacaoPage";
import CentralAjustesPage from "./pages/CentralAjustesPage";
import "./App.css";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#A3A3A3]">Carregando...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    // Gestora tem landing operacional em /clientes; demais vão pro dashboard.
    const fallback = user.role === "gestora" ? "/clientes" : "/painel";
    return <Navigate to={fallback} replace />;
  }
  return children;
}

// Wrapper específico para /painel: gestora não tem dashboard estratégico.
function DashboardRoute() {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#A3A3A3]">Carregando...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "gestora") return <Navigate to="/clientes" replace />;
  return <DashboardPage />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" />
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/cadastro" element={<PublicRegistrationPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/painel" element={<Protected><DashboardRoute /></Protected>} />
          <Route path="/clientes" element={<Protected><ClientesPage /></Protected>} />
          <Route path="/clientes/bloqueados" element={<Protected roles={["admin", "gestora"]}><BlockedClientesPage /></Protected>} />
          <Route path="/clientes/:id" element={<Protected><ClientDetailPage /></Protected>} />
          <Route path="/contratos" element={<Protected roles={["admin","gestora","socio"]}><ContratosPage /></Protected>} />
          <Route path="/parcelados" element={<Protected roles={["admin","gestora","socio"]}><ParceladosPage /></Protected>} />
          <Route path="/parcelados/:id" element={<Protected roles={["admin","gestora","socio"]}><ParceladoDetailPage /></Protected>} />
          <Route path="/vencimentos" element={<Protected roles={["admin","gestora","socio"]}><VencimentosPage /></Protected>} />
          <Route path="/cobranca" element={<Protected roles={["admin","gestora","socio"]}><CobrancaPage /></Protected>} />
          <Route path="/recuperacao" element={<Protected roles={["admin","gestora","socio"]}><RecuperacaoPage /></Protected>} />
          <Route path="/dashboard/executivo" element={<Protected roles={["admin"]}><ExecutiveDashboardPage /></Protected>} />
          <Route path="/fechamento" element={<Protected roles={["admin"]}><FechamentoPage /></Protected>} />
          <Route path="/admin/capital-proprio" element={<Protected roles={["admin"]}><CapitalSourcePage /></Protected>} />
          <Route path="/admin/recalcular-status" element={<Protected roles={["admin"]}><RecalcStatusPage /></Protected>} />
          <Route path="/admin/juros-rapido" element={<Protected roles={["admin"]}><HighInterestPage /></Protected>} />
          <Route path="/admin/auditoria" element={<Protected roles={["admin"]}><AuditoriaPage /></Protected>} />
          <Route path="/admin/central-ajustes" element={<Protected roles={["admin"]}><CentralAjustesPage /></Protected>} />
          <Route path="/whatsapp" element={<Protected roles={["admin"]}><WhatsAppPage /></Protected>} />
          <Route path="/integracoes" element={<Protected roles={["admin"]}><IntegracoesPage /></Protected>} />
          <Route path="/integracoes/:code" element={<Protected roles={["admin"]}><IntegracaoDetailPage /></Protected>} />
          <Route path="/comunicacao" element={<Protected roles={["admin"]}><ComunicacaoPage /></Protected>} />
          <Route path="/financeiro" element={<Protected roles={["admin","socio"]}><FinancialDashboardPage /></Protected>} />
          <Route path="/previsao" element={<Protected roles={["admin","socio"]}><ForecastPage /></Protected>} />
          <Route path="/socios" element={<Protected roles={["admin"]}><SociosPage /></Protected>} />
          <Route path="/tesouraria" element={<Protected roles={["admin","socio"]}><TreasuryPage /></Protected>} />
          <Route path="/comissoes" element={<Protected roles={["admin","indicador"]}><CommissionsPage /></Protected>} />
          <Route path="/indicadores" element={<Protected roles={["admin"]}><IndicatorsPage /></Protected>} />
          <Route path="/usuarios" element={<Protected roles={["admin"]}><UsuariosPage /></Protected>} />
          <Route path="/importacao" element={<Protected roles={["admin"]}><ImportPage /></Protected>} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
